import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiClock,
  FiHash,
  FiInbox,
} from "react-icons/fi";
import { GiSoccerBall } from "react-icons/gi";
import bettingService from "../../../api/services/bettingService";
import { playSong } from "../../common/actions/common.actions";

const OldMatchesPage = ({ setIsLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { oldHistory } = useSelector((state) => state.sidebar);
  const [oldMatches, setOldMatches] = useState([]);
  const [hasError, setHasError] = useState(false);

  const language = localStorage.getItem("language");
  const betAmount = Number(oldHistory?.amount || 0);
  const profitAmount = Math.max(0, Number(oldHistory?.profitAmount || 0));
  const winAmount = Math.max(profitAmount - betAmount, 0);
  const betId = oldHistory?.postingNo || id;

  const formatAmount = (value) =>
    Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const handleBackClick = () => {
    dispatch(playSong());
    navigate("/old-histories");
  };

  useEffect(() => {
    setIsLoading(true);
    setHasError(false);

    bettingService
      .bettingHistoryDetails({ gamblingId: Number(id), status: "Finished" })
      .then((response) => response.json())
      .then((data) => {
        const matches = Array.isArray(data) ? data : [];
        const sortedMatches = matches.slice().sort((a, b) => {
          const parseDate = (value = "") => {
            const [date = "", time = ""] = value.split(" ");
            return new Date(`${date.split("-").reverse().join("-")} ${time}`);
          };

          return parseDate(a?.eventDate) - parseDate(b?.eventDate);
        });
        setOldMatches(sortedMatches);
      })
      .catch((error) => {
        setOldMatches([]);
        setHasError(true);
        console.error("API request error:", error);
      })
      .finally(() => setIsLoading(false));
  }, [id, setIsLoading]);

  return (
    <div className="relative min-h-screen w-full min-w-0 flex-1 self-stretch overflow-hidden bg-gradient_start font-myanmarSansPro text-white">
      <div className="pointer-events-none absolute -right-24 top-20 h-72 w-72 rounded-full bg-emerald-300/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-28 bottom-12 h-64 w-64 rounded-full bg-black/15 blur-3xl" />

      <header className="relative z-30 bg-header_green shadow-lg shadow-black/20">
        <div className="mx-auto flex min-h-[60px] w-full max-w-2xl items-center px-4 py-2">
          <button
            type="button"
            onClick={handleBackClick}
            aria-label="Back to betting history"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-lg border border-white/10 bg-white/10 transition hover:bg-white/20 active:scale-95"
          >
            <FiArrowLeft className="h-5 w-5" />
          </button>
          <div className="ml-3 min-w-0">
            <h1 className="truncate text-base font-semibold leading-[1.8]">
              {t("oldMatches")}
            </h1>
            <p className="flex items-center gap-1 font-sans text-[10px] uppercase tracking-[0.16em] text-white/40">
              <FiCheckCircle className="h-3 w-3" /> Finished
            </p>
          </div>
          {/* <div className="ml-auto flex max-w-[46%] items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 font-sans text-[11px] text-white/55">
            <FiHash className="h-3.5 w-3.5 flex-none text-emerald-200" />
            <span className="truncate">{betId}</span>
          </div> */}
        </div>
      </header>

      <main className="relative z-10 mx-auto flex h-[calc(100vh-60px)] w-full max-w-2xl flex-col px-4 pb-3 pt-3">
        <div className="mb-3 flex flex-none items-center justify-between px-1">
          <div className="flex items-center gap-2 text-xs text-white/55">
            <GiSoccerBall className="h-4 w-4 text-emerald-100" />
            <span>{oldMatches.length} {t("matches")}</span>
          </div>
          <span className="font-sans text-[10px] uppercase tracking-[0.14em] text-white/70">
            Bet ID · {betId}
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-3">
          {oldMatches.length > 0 ? (
            <div className="space-y-3">
              {oldMatches.map((match, index) => (
                <MatchCard
                  key={match?.bettingId || `${match?.eventDate}-${index}`}
                  match={match}
                  language={language}
                  t={t}
                  position={index + 1}
                />
              ))}
            </div>
          ) : (
            <div className="flex h-full min-h-[260px] flex-col items-center justify-center px-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-white/10 bg-header_green/50 text-white/45">
                <FiInbox className="h-6 w-6" />
              </div>
              <p className="mt-4 max-w-xs text-sm leading-[1.8] text-white/65">
                {hasError ? t("sendingFail") : t("noData")}
              </p>
              <p className="mt-1 font-sans text-xs text-white/35">#{betId}</p>
            </div>
          )}
        </div>

        <section className="flex-none rounded-lg border border-white/10 bg-header_green/80 p-2.5 shadow-2xl shadow-black/25 backdrop-blur-xl">
          <div className="grid grid-cols-3 divide-x divide-white/10">
            <SummaryItem label={t("betAmount")} value={formatAmount(betAmount)} />
            <SummaryItem
              label={t("profitAmount")}
              value={formatAmount(profitAmount)}
            />
            <SummaryItem
              label={t("winAmount")}
              value={formatAmount(winAmount)}
              highlighted
            />
          </div>
        </section>
      </main>
    </div>
  );
};

const MatchCard = ({ match, language, t, position }) => {
  const bettedTeam =
    language === "en"
      ? match?.bettedTeam
      : match?.bettedTeamMyan || match?.bettedTeam;
  const oppositeTeam =
    language === "en"
      ? match?.oppositeTeamName
      : match?.oppositeTeamNameMyan || match?.oppositeTeamName;
  const homeTeam = match?.isHome ? bettedTeam : oppositeTeam;
  const awayTeam = match?.isHome ? oppositeTeam : bettedTeam;
  const hasBodyOdd = match?.bodyOdd && String(match.bodyOdd) !== "0";

  const selection = match?.overs
    ? t("goalOver")
    : match?.under
    ? t("goalUnder")
    : bettedTeam;

  const selectionOdd = match?.overs || match?.under
    ? match?.goalOdd
    : hasBodyOdd
    ? match?.bodyOdd
    : null;

  const showBodyOdd = !match?.overs && !match?.under && hasBodyOdd;

  return (
    <article className="overflow-hidden rounded-lg border border-primary/40 bg-header_green/55 shadow-lg shadow-black/15 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-white/10 px-3.5 py-2">
        <div className="flex min-w-0 items-center gap-2 font-sans text-[11px] text-white/45">
          <span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-white/10 text-[10px] font-semibold text-white/70">
            {position}
          </span>
          <FiClock className="h-3.5 w-3.5 flex-none" />
          <span className="truncate">{match?.eventDate || "—"}</span>
        </div>

      </div>

      <div className="px-3.5 py-1">
        <TeamRow
          name={homeTeam}
          score={match?.homeResult}
          selected={Boolean(match?.home)}
          odd={showBodyOdd && match?.isHomeBodyOdd ? match?.bodyOdd : null}
        />
        <div className="my-1 border-t border-dashed border-white/10" />
        <TeamRow
          name={awayTeam}
          score={match?.awayResult}
          selected={Boolean(match?.away)}
          odd={showBodyOdd && !match?.isHomeBodyOdd ? match?.bodyOdd : null}
        />
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-white/10 bg-black/10 px-3.5 py-2.5">
        <span className="text-[10px] leading-[1.8] text-white/80">{t("selectedMatches")}</span>
        <div className="flex min-w-0 items-center gap-2">
          {selectionOdd !== null &&
            selectionOdd !== undefined &&
            (match?.overs || match?.under) && (
              <span className="flex-none rounded-md border border-amber-200/15 bg-amber-200/10 px-2 py-1 font-sans text-[10px] font-semibold text-amber-200">
                {selectionOdd}
              </span>
            )}
          <FiCheckCircle className="h-4 w-4 flex-none text-emerald-300" />
          <span className="truncate text-xs font-semibold leading-[1.8] text-wback">
            {selection || "—"}
          </span>
        </div>
      </div>
    </article>
  );
};

const TeamRow = ({ name, score, selected, odd }) => (
  <div className="flex min-h-[32px] items-center gap-3">
    <span className={`min-w-0 flex-1 text-sm leading-[1.8]`}>
      {name || "—"}
      {odd !== null && odd !== undefined && (
        <span className="ml-2 font-sans text-[11px] font-semibold text-amber-200">({odd})</span>
      )}
    </span>
    <span className={`flex h-6 min-w-[30px] items-center justify-center rounded-md font-sans text-sm font-bold`}>
      {score === "NS" || score === null || score === undefined ? "—" : score}
    </span>
  </div>
);

const SummaryItem = ({ label, value, highlighted }) => (
  <div className="min-w-0 px-2 text-center first:pl-0 last:pr-0">
    <p className="truncate text-[9px] leading-[1.8] text-white/40">{label}</p>
    <p className={`mt-1 truncate font-sans text-sm font-semibold ${highlighted ? "text-emerald-200" : "text-white"}`}>
      {value}
    </p>
  </div>
);

export default OldMatchesPage;
